import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Check,
  ArrowRight,
  Users,
  Building2,
  Shield,
  Zap,
  Headphones,
  CreditCard,
  Plus,
  Minus,
  ArrowLeft,
} from "lucide-react";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import billingService from "../../../services/billingService";
import * as localService from "../../../services/localService";
import { HostClientsConstants, Constants } from "../../../utils/constants";
import { useCurrencies } from "../../../hooks/useCurrencies";
import { Button } from "../../../shadcn/ui/button";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import {
  checkActiveSubscription,
  calculateSubscriptionCost,
  extractCheckoutUrl,
  getSubscriptionQuantity,
  isQuantityEditable,
  getCurrencyCode,
  formatCurrencyDisplay,
} from "../../../utils/subscriptionUtils";
import { isOrgAdmin } from "../../../utils/orgAdminAccess";
import ContactAdminBanner from "../../../components/ContactAdminBanner";
import ContactAdminModal from "../../../components/ContactAdminModal";

// Import background image (same as host pricing)
const pricingBg = require("../../../assets/img/pricingbg.png");

const HostSubscriptionUpgrade = () => {
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [_packages, setPackages] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { currencies } = useCurrencies();

  // Get current user from localStorage
  useEffect(() => {
    const user = localService.getUser();
    setCurrentUser(user);
  }, []);

  // Log currencies when they change
  useEffect(() => {
    logger.log("HOST SUBSCRIPTION UPGRADE: Currencies updated, count:", currencies.length);
    if (currencies.length > 0) {
      logger.log("Sample currencies:", currencies.slice(0, 3));
    }
  }, [currencies]);

  // Get parameters from URL
  const urlParams = new URLSearchParams(location.search);
  const packageId = urlParams.get("packageId");

  // Fetch packages and selected package
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setIsLoading(true);

        // Determine package type based on user role
        // Host roles (who create custom packages) see STANDARD packages to subscribe to
        // Customer roles (who receive custom packages) see CUSTOM packages
        const hostRoles = HostClientsConstants.HOST_ROLES;
        const customerRoles = HostClientsConstants.HOST_CUSTOMER_ROLES;

        let _packageType = "STANDARD"; // Default
        if (customerRoles.includes(currentUser?.organizationRole?.code)) {
          _packageType = "CUSTOM"; // Customers see custom packages created by hosts
        } else if (hostRoles.includes(currentUser?.organizationRole?.code)) {
          _packageType = "STANDARD"; // Hosts see standard packages to subscribe to
        }

        const response = await billingService.getApplicablePackages({
          // type: packageType, // Remove type parameter as it's not supported
          // detailed: true, // Remove detailed parameter as it's not supported
          limit: 10,
        });

        if (response.data?.data) {
          const packagesData = response.data.data;
          setPackages(packagesData);

          // Find selected package if packageId is provided
          if (packageId) {
            const selected = packagesData.find(
              pkg => pkg.id === packageId || pkg.id === parseInt(packageId) || pkg.id === packageId.toString()
            );
            logger.log("Looking for packageId:", packageId);
            logger.log(
              "Available packages:",
              packagesData.map(p => ({ id: p.id, name: p.name }))
            );
            logger.log("Selected package:", selected);
            setSelectedPackage(selected);
            // Initialize quantity based on selected package
            if (selected && currentUser) {
              // For add-ons (recurring = false), always start with quantity 1
              // For recurring packages, use the calculated quantity
              const isAddOn = selected.recurring === false;
              const initialQuantity = isAddOn ? 1 : getSubscriptionQuantity(currentUser, selected);
              setQuantity(initialQuantity);
            }
          } else if (packagesData.length > 0) {
            // Default to first package if none selected
            const firstPackage = packagesData[0];
            setSelectedPackage(firstPackage);
            // Initialize quantity based on package
            // For add-ons (recurring = false), always start with quantity 1
            const isAddOn = firstPackage.recurring === false;
            const initialQuantity = isAddOn ? 1 : getSubscriptionQuantity(currentUser, firstPackage);
            setQuantity(initialQuantity);
          } else {
            // No packages available
            setSelectedPackage(null);
          }
        } else {
          // No packages in response
          setPackages([]);
          setSelectedPackage(null);
        }
      } catch (error) {
        logger.error("Error fetching packages:", error);
        showApiMessage(error, "load packages");
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      fetchPackages();
    }
  }, [packageId, currentUser]);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const userIsOrgAdmin = isOrgAdmin(currentUser);

  const handleGetStarted = async pkg => {
    if (!userIsOrgAdmin) {
      setContactModalOpen(true);
      return;
    }

    try {
      setIsUpgrading(true);

      if (!currentUser?.organizationId) {
        throw new Error("Organization ID not found. Please ensure you're logged in.");
      }

      // For add-ons (recurring = false), always use the quantity state
      // For recurring packages, use quantity state if editable, otherwise use calculated quantity
      const isAddOn = pkg?.recurring === false;
      const subscriptionQuantity = getSubscriptionQuantity(currentUser, pkg);
      const finalQuantity = isAddOn ? quantity : isQuantityEditable(currentUser, pkg) ? quantity : subscriptionQuantity;

      const userRole = currentUser?.organizationRole?.code || currentUser?.role || "";

      // Check if user has an active subscription
      const activeSubscriptionCheck = await checkActiveSubscription(currentUser);

      const isRecurringPackage = pkg?.recurring !== false;

      if (
        isRecurringPackage &&
        activeSubscriptionCheck.hasActiveSubscription &&
        activeSubscriptionCheck.activeSubscription?.id
      ) {
        // User has active subscription - use CHANGE API
        logger.log("Changing existing subscription:", {
          organizationId: currentUser.organizationId,
          subscriptionId: activeSubscriptionCheck.activeSubscription.id,
          packageId: pkg.id,
          quantity: finalQuantity,
          userRole: userRole,
        });

        const response = await billingService.changeOrganizationSubscription({
          organizationId: currentUser.organizationId,
          subscriptionId: activeSubscriptionCheck.activeSubscription.id,
          packageId: pkg.id,
          quantity: finalQuantity,
        });

        logger.log("Change subscription response:", response);

        // Check for success message
        if (response?.data?.message) {
          showApiMessage(response, "change subscription");
        }

        // Extract checkout URL using shared utility
        const checkoutUrl = extractCheckoutUrl(response);

        if (checkoutUrl && typeof checkoutUrl === "string") {
          // Direct navigation to checkout URL
          window.location.href = checkoutUrl;
          return;
        }

        // If no checkout URL, show success and redirect to dashboard
        showApiMessage(
          {
            data: {
              message: "Subscription updated successfully! Redirecting to dashboard...",
            },
          },
          "change subscription"
        );

        setTimeout(() => {
          history.push("/host/dashboard");
        }, 2000);
      } else {
        // User has no active subscription - use CREATE API
        logger.log("Creating new subscription:", {
          organizationId: currentUser.organizationId,
          packageId: pkg.id,
          quantity: finalQuantity,
          userRole: userRole,
        });

        const response = await billingService.createOrganizationSubscription({
          organizationId: currentUser.organizationId,
          packageId: pkg.id,
          quantity: finalQuantity,
        });

        logger.log("Create subscription response:", response);

        // Check for success message
        if (response?.data?.message) {
          showApiMessage(response, "create subscription");
        }

        // Extract checkout URL using shared utility
        const checkoutUrl = extractCheckoutUrl(response);

        if (checkoutUrl && typeof checkoutUrl === "string") {
          // Direct navigation to checkout URL
          window.location.href = checkoutUrl;
          return;
        }

        // If no checkout URL, show success and redirect to dashboard
        showApiMessage(
          {
            data: {
              message: "Subscription created successfully! Redirecting to dashboard...",
            },
          },
          "create subscription"
        );

        setTimeout(() => {
          history.push("/host/dashboard");
        }, 2000);
      }
    } catch (error) {
      logger.error("Subscription error:", error);
      showApiMessage(error, "subscription");
    } finally {
      setIsUpgrading(false);
    }
  };

  // Currency formatting helper - uses shared utility
  const formatCurrency = (amount, currencyIdOrCode = null) => {
    const currencyCode = getCurrencyCode(currencyIdOrCode || "KES", currencies);
    return formatCurrencyDisplay(amount, currencyCode);
  };

  const getPackageFeatures = pkg => {
    const features = [];

    if (pkg.maximumTeamSize) {
      features.push({
        text: `${pkg.maximumTeamSize} team members`,
        icon: Users,
        description: "Maximum number of team members allowed",
      });
    }

    if (pkg.maximumServiceProvidersAllowed) {
      features.push({
        text: `${pkg.maximumServiceProvidersAllowed} service providers`,
        icon: Building2,
        description: "Maximum number of service providers",
      });
    }

    if (pkg.offers && pkg.offers.length > 0) {
      pkg.offers.forEach(offer => {
        if (offer.name) {
          features.push({
            text: offer.name,
            icon: Zap,
            description: offer.description || "Included service offer",
          });
        }
      });
    }

    // Add default features if none exist
    if (features.length === 0) {
      features.push(
        {
          text: "Core features included",
          icon: Check,
          description: "All essential platform features",
        },
        {
          text: "Basic support",
          icon: Headphones,
          description: "Email and chat support",
        },
        {
          text: "Standard templates",
          icon: Building2,
          description: "Access to standard templates",
        },
        {
          text: "Email support",
          icon: Check,
          description: "24/7 email support",
        },
        {
          text: "Cloud storage",
          icon: Shield,
          description: "Secure cloud storage included",
        }
      );
    }

    return features;
  };

  if (isLoading) {
    return (
      <div
        className="overflow-hidden relative min-h-screen"
        style={{
          backgroundImage: `url(${pricingBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <div className="relative z-10 px-4 py-8 mx-auto max-w-6xl sm:py-12">
          <div className="animate-pulse">
            <div className="mb-4 w-1/3 h-8 bg-gray-200 rounded"></div>
            <div className="mb-16 w-1/2 h-6 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <div className="p-8 rounded-lg border border-gray-200 bg-white">
                  <div className="mb-4 h-6 bg-gray-200 rounded"></div>
                  <div className="mb-6 h-12 bg-gray-200 rounded"></div>
                  <div className="mb-8 h-4 bg-gray-200 rounded"></div>
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map(j => (
                      <div key={j} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="p-8 rounded-lg border border-gray-200 bg-white">
                  <div className="mb-4 h-6 bg-gray-200 rounded"></div>
                  <div className="mb-6 h-12 bg-gray-200 rounded"></div>
                  <div className="mb-8 h-12 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedPackage) {
    const hasNoPackages = _packages.length === 0;

    return (
      <div
        className="overflow-hidden relative min-h-screen"
        style={{
          backgroundImage: `url(${pricingBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 px-4 py-8 mx-auto max-w-6xl sm:py-12">
          <div className="text-center">
            {hasNoPackages ? (
              <>
                <Building2 className="w-16 h-16 mx-auto mb-4 text-white" />
                <h3 className="mb-2 text-2xl font-semibold text-white">No packages available</h3>
                <p className="mb-6 text-gray-200">
                  There are currently no subscription packages available for your account.
                </p>
                <p className="mb-6 text-sm text-gray-300">
                  Please contact support or try logging out and back in if you believe this is an error.
                </p>
              </>
            ) : (
              <>
                <h3 className="mb-2 text-xl font-semibold text-white">No package selected</h3>
                <p className="mb-6 text-gray-200">Please select a package to view its details.</p>
              </>
            )}
            <button
              onClick={() => history.goBack()}
              className="px-6 py-3 text-gray-900 bg-white rounded-md transition-colors hover:bg-gray-100"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const features = getPackageFeatures(selectedPackage);

  // Calculate pricing using shared utility
  // For add-ons, always use quantity state. For recurring packages, use quantity state if editable, otherwise use calculated quantity
  const isAddOn = selectedPackage?.recurring === false;
  const calculatedQuantity = getSubscriptionQuantity(currentUser, selectedPackage);
  const finalQuantityForPricing = isAddOn
    ? quantity
    : isQuantityEditable(currentUser, selectedPackage)
      ? quantity
      : calculatedQuantity;
  const { perUserCost, totalCost } = calculateSubscriptionCost(selectedPackage, finalQuantityForPricing);

  return (
    <div
      className="overflow-hidden relative min-h-screen"
      style={{
        backgroundImage: `url(${pricingBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <div className="relative z-10 px-4 py-8 mx-auto max-w-6xl sm:py-12">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={() => history.goBack()}
            className="flex gap-2 items-center mb-6 text-gray-700 hover:text-gray-900"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to packages
          </button>
          <h1 className="mb-4 text-3xl font-semibold text-gray-900">{selectedPackage.name}</h1>
          <p className="max-w-2xl text-lg text-gray-700">
            {selectedPackage.description ||
              "Comprehensive package with all the features you need to scale your business"}
          </p>

          {!userIsOrgAdmin && (
            <div className="mt-6">
              <ContactAdminBanner user={currentUser} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
          {/* Package Details */}
          <div className="lg:col-span-2">
            {/* Pricing Section */}
            <div className="p-8 mb-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">Pricing</h2>

              {/* Per User Cost */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Per user cost:</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {formatCurrency(perUserCost, selectedPackage.currencyId)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {selectedPackage.recurring === false ? "Quantity:" : "Number of users:"}
                  </span>
                  <span className="text-lg font-semibold text-gray-900">{finalQuantityForPricing}</span>
                </div>
              </div>

              {/* Total Cost */}
              <div className="flex gap-3 items-baseline mb-4">
                <span className="text-5xl font-bold text-gray-900">
                  {formatCurrency(totalCost, selectedPackage.currencyId)}
                </span>
                <span className="text-xl text-gray-600">{selectedPackage.recurring ? "/month" : "one-time"}</span>
              </div>
              {selectedPackage.recurring && <p className="text-gray-600">Billed monthly • Cancel anytime</p>}
              <p className="text-sm text-gray-500 mt-2">
                {selectedPackage.recurring === false
                  ? `Total for ${finalQuantityForPricing} item${finalQuantityForPricing > 1 ? "s" : ""}`
                  : `Total for ${finalQuantityForPricing} user${finalQuantityForPricing > 1 ? "s" : ""}`}
              </p>
            </div>

            {/* Features Section */}
            <div className="mb-8 p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <h2 className="mb-6 text-2xl font-semibold text-gray-900">What's included</h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <div key={index} className="flex gap-4 items-start">
                      <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                        <IconComponent className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-medium text-gray-900">{feature.text}</h3>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Package Details */}
            <div className="mb-8 p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <h2 className="mb-6 text-2xl font-semibold text-gray-900">Package details</h2>
              <div className="p-6 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="mb-2 font-medium text-gray-900">Package Information</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>Package ID:</span>
                        <span className="font-mono">{selectedPackage.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Code:</span>
                        <span>{selectedPackage.code || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Type:</span>
                        <span className="capitalize">{selectedPackage.type?.toLowerCase() || "Standard"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Version:</span>
                        <span>{selectedPackage.version || "1.0"}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 font-medium text-gray-900">Limits & Restrictions</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>Max Team Size:</span>
                        <span>{selectedPackage.maximumTeamSize || "Unlimited"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Max Service Providers:</span>
                        <span>{selectedPackage.maximumServiceProvidersAllowed || "Unlimited"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Recurring:</span>
                        <span>{selectedPackage.recurring ? "Yes" : "No"}</span>
                      </div>
                      {selectedPackage.validity && (
                        <div className="flex justify-between">
                          <span>Validity:</span>
                          <span>
                            {selectedPackage.validity} {selectedPackage.validityTimeUnit?.toLowerCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Offers Section */}
            {selectedPackage.offers && selectedPackage.offers.length > 0 && (
              <div className="mb-8 p-8 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <h2 className="mb-6 text-2xl font-semibold text-gray-900">Included offers</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {selectedPackage.offers.map((offer, index) => (
                    <div key={index} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                      <h3 className="mb-2 font-medium text-gray-900">{offer.name}</h3>
                      <p className="text-sm text-gray-600">
                        {offer.description || "Service offer included in this package"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Subscribe Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="p-8 rounded-2xl border border-gray-100 bg-white shadow-sm">
                <h3 className="mb-4 text-xl font-semibold text-gray-900">Ready to subscribe?</h3>

                <div className="mb-6">
                  {/* Quantity Input - Always show for add-ons, conditional for recurring packages */}
                  {(selectedPackage.recurring === false || isQuantityEditable(currentUser, selectedPackage)) && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <Label htmlFor="quantity" className="text-sm font-medium text-gray-700 mb-2 block">
                        {selectedPackage.recurring === false ? "Quantity" : "Number of Users"}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 flex-shrink-0"
                          onClick={() => {
                            const newQuantity = Math.max(1, quantity - 1);
                            setQuantity(newQuantity);
                          }}
                          disabled={selectedPackage.recurring === false ? false : quantity <= 1}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <Input
                          id="quantity"
                          type="number"
                          min="1"
                          value={quantity}
                          onChange={e => {
                            const newQuantity = parseInt(e.target.value) || 1;
                            setQuantity(Math.max(1, newQuantity));
                          }}
                          className="w-full text-center"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 flex-shrink-0"
                          onClick={() => {
                            setQuantity(quantity + 1);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {selectedPackage.maximumTeamSize && (
                        <p className="text-xs text-gray-500 mt-1">
                          Maximum: {selectedPackage.maximumTeamSize}{" "}
                          {selectedPackage.recurring === false ? "items" : "users"}
                        </p>
                      )}
                    </div>
                  )}

                  {selectedPackage.recurring !== false && !isQuantityEditable(currentUser, selectedPackage) && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-800">
                        {selectedPackage.maximumTeamSize > 1
                          ? "Fixed team size"
                          : `Matches your team size (${quantity} users)`}
                      </span>
                    </div>
                  )}

                  {/* Breakdown */}
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {selectedPackage.recurring === false ? "Per item:" : "Per user:"}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(perUserCost, selectedPackage.currencyId)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {selectedPackage.recurring === false ? "Quantity:" : "Users:"}
                      </span>
                      <span className="font-medium text-gray-900">× {finalQuantityForPricing}</span>
                    </div>
                    <div className="h-px bg-gray-200"></div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700 font-medium">Total:</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(totalCost, selectedPackage.currencyId)}
                      </span>
                    </div>
                  </div>

                  {/* Large Total Display */}
                  <div className="flex gap-2 items-baseline mb-2">
                    <span className="text-3xl font-bold text-gray-900">
                      {formatCurrency(totalCost, selectedPackage.currencyId)}
                    </span>
                    <span className="text-gray-600">{selectedPackage.recurring ? "/month" : "one-time"}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {selectedPackage.recurring
                      ? `Billed monthly for ${finalQuantityForPricing} user${finalQuantityForPricing > 1 ? "s" : ""} • Cancel anytime`
                      : `One-time payment for ${finalQuantityForPricing} item${finalQuantityForPricing > 1 ? "s" : ""}`}
                  </p>
                </div>

                <Button
                  onClick={() => handleGetStarted(selectedPackage)}
                  disabled={isUpgrading}
                  className="px-6 py-4 mb-6 w-full text-lg font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#2d2bb8")}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = Constants.HOST_EXPERIENCE_BASE_COLOR)}
                >
                  {isUpgrading ? (
                    <div className="flex gap-2 justify-center items-center">
                      <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                      Processing...
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-center items-center">
                      <CreditCard className="w-5 h-5" />
                      {userIsOrgAdmin ? "Subscribe Now" : "Contact Admin to Subscribe"}
                    </div>
                  )}
                </Button>

                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex gap-2 items-center">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>30-day money-back guarantee</span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Cancel anytime</span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>24/7 support</span>
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    {currentUser?.organizationRole?.code === "ROLE_HOST_CUSTOMER_INDIVIDUAL" &&
                      "Custom package created by your service provider"}
                    {currentUser?.organizationRole?.code === "ROLE_SUBSCRIBER_HOST" &&
                      "Standard package available for subscription"}
                    {currentUser?.organizationRole?.code === "ROLE_ADMINISTRATOR" &&
                      "Standard package managed by platform"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactAdminModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        user={currentUser}
        packageName={selectedPackage?.name}
      />
    </div>
  );
};

export default HostSubscriptionUpgrade;
